/**
 * @description
 * BankLetteringComponent provides an interactive interface for Healthcare / FSE bank reconciliation
 * and NOEMIE RSP lettering. It enables operators to record an incoming CPAM / Mutuelle bank
 * statement line (date, method, reference, total received) and allocate amounts across one or multiple
 * open third-party payments (AMO / AMC) with real-time balancing controls and BOFIP cryptographic sealing.
 *
 * Called via route: `/fse/bank-lettering`
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { finalize } from 'rxjs';
import Swal from 'sweetalert2';

import { AlertService } from '@app/shared/alert.service';
import { LoginService } from '@app/common/login.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { FormatFSECurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import * as util from '@app/shared/util';
import * as fselib from 'mercalink-fselib';
import { environment } from '../../../environments/environment';

import { FseService, BankLetteringLot } from '../fse.service';

/**
 * Filter configuration for open third-party payments list.
 */
export interface FseReceivablesFilter {
  /** Filter by part type */
  partFilter: 'ALL' | 'AMO' | 'AMC';
  /** Search query across docRef, order, patient, organism, wire ref */
  search: string;
}

/**
 * Bank settlement header form model.
 */
export interface BankStatementForm {
  /** Date of bank settlement */
  dateReglement: Date;
  /** Payment method ID (c_values 7, default 6 for Virement) */
  paymentMethod: number;
  /** Bank account / IBAN label */
  bankAccount: string;
  /** Bank statement reference / transfer ID */
  bankReference: string;
  /** Total received amount in € */
  totalAmount: number | null;
  /** Internal note / memo */
  note: string;
}

@Component({
  selector: 'app-fse-bank-lettering',
  standalone: true,
  templateUrl: './bank-lettering.component.html',
  styleUrls: ['./bank-lettering.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule,
    MatCardModule,
    MenuCommonComponent,
    SelectDateComponent,
    FormatFSECurrencyPipe
  ]
})
export class BankLetteringComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private fseService = inject(FseService);
  private loginService = inject(LoginService);
  private alertService = inject(AlertService);
  private spinner = inject(SpinnerService);

  public util = util;

  /** List of fetched open draft payments (AMO / AMC) */
  public lots: BankLetteringLot[] = [];

  /** List of RSP anomalies / errors */
  public errorLots: fselib.FseRsp[] = [];

  /** Selected error RSP for diagnostic panel */
  public selectedErrorRsp: fselib.FseRsp | null = null;

  /** Active tab selection ('lettering' | 'errors') */
  public activeTab: 'lettering' | 'errors' = 'lettering';

  /** Loading state indicator */
  public isLoading: boolean = false;

  /** Submitting state indicator */
  public isSubmitting: boolean = false;

  /** Set of selected payment IDs */
  public selectedPaymentIds: Set<number> = new Set<number>();

  /** Map of allocated amounts per payment ID */
  public allocatedAmounts: Map<number, number> = new Map<number, number>();

  /** Receivables filter model */
  public filter: FseReceivablesFilter = {
    partFilter: 'ALL',
    search: ''
  };

  /** Bank settlement header form */
  public statement: BankStatementForm = {
    dateReglement: new Date(),
    paymentMethod: 6, // Default: Virement (id 6 in c_values 7)
    bankAccount: '',
    bankReference: '',
    totalAmount: null,
    note: ''
  };

  ngOnInit(): void {
    this.loadRspTransfers();
    this.loadRspErrors();
  }

  public selectTab(tab: 'lettering' | 'errors'): void {
    this.activeTab = tab;
    if (tab === 'errors' && this.errorLots.length === 0) {
      this.loadRspErrors();
    }
    this.cdr.markForCheck();
  }

  /**
   * Load all open third-party payments in DRAFT_TP state.
   */
  public loadRspTransfers(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.fseService.getBankLetteringLots$()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (lots) => {
          this.lots = lots;
          // Prune selection if any selected payment is no longer open
          const availableIds = new Set(lots.map(l => l.paymentId));
          for (const id of Array.from(this.selectedPaymentIds)) {
            if (!availableIds.has(id)) {
              this.selectedPaymentIds.delete(id);
              this.allocatedAmounts.delete(id);
            }
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.alertService.error('Erreur lors du chargement des règlements: ' + err.message);
        }
      });
  }

  /**
   * Load all RSPs in ERROR state.
   */
  public loadRspErrors(): void {
    this.fseService.getBankLetteringErrors$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (errors) => {
          this.errorLots = errors;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erreur lors du chargement des anomalies RSP:', err);
          this.alertService.error('Erreur lors du chargement des anomalies RSP: ' + (err.message || err));
        }
      });
  }

  public selectErrorRsp(rsp: fselib.FseRsp): void {
    this.selectedErrorRsp = rsp;
    this.cdr.markForCheck();
  }

  public onRetryLettering(): void {
    this.spinner.show();
    this.fseService.postFseRspLetteringEvent$(environment.server_url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService.success('Traitement et lettrage des RSP relancé avec succès.');
          this.loadRspTransfers();
          this.loadRspErrors();
          this.selectedErrorRsp = null;
          this.spinner.hide();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.alertService.error('Erreur lors du relancement du lettrage: ' + err.message);
          this.spinner.hide();
        }
      });
  }

  /**
   * Filtered lots list according to active search and part type filters.
   */
  public get filteredLots(): BankLetteringLot[] {
    let result = this.lots;

    if (this.filter.partFilter !== 'ALL') {
      result = result.filter(l => l.partType === this.filter.partFilter);
    }

    if (this.filter.search && this.filter.search.trim()) {
      const q = this.filter.search.toLowerCase().trim();
      result = result.filter(l =>
        (l.fseNumber && l.fseNumber.toLowerCase().includes(q)) ||
        (l.patientName && l.patientName.toLowerCase().includes(q)) ||
        (l.numSecu && l.numSecu.toLowerCase().includes(q)) ||
        (l.payerName && l.payerName.toLowerCase().includes(q)) ||
        (l.organismCode && l.organismCode.toLowerCase().includes(q)) ||
        (l.libVirement && l.libVirement.toLowerCase().includes(q))
      );
    }

    return result;
  }

  /**
   * Handle bank statement date change.
   */
  public onDateChange(newDate: Date): void {
    this.statement.dateReglement = newDate;
    this.cdr.markForCheck();
  }

  /**
   * Toggle selection state of a payment.
   */
  public togglePaymentSelection(lot: BankLetteringLot): void {
    const id = lot.paymentId;
    if (this.selectedPaymentIds.has(id)) {
      this.selectedPaymentIds.delete(id);
      this.allocatedAmounts.delete(id);
    } else {
      this.selectedPaymentIds.add(id);
      const amt = Number(lot.amount) || 0;
      this.allocatedAmounts.set(id, amt);

      // Auto-prefill statement header if empty on first selection
      if (!this.statement.totalAmount || this.statement.totalAmount === 0) {
        this.statement.totalAmount = amt;
      }
      if (!this.statement.bankReference && lot.libVirement) {
        this.statement.bankReference = lot.libVirement;
      }
      if (lot.dateComptable && (!this.statement.dateReglement || this.selectedPaymentIds.size === 1)) {
        this.statement.dateReglement = new Date(lot.dateComptable);
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Check if a payment is selected.
   */
  public isPaymentSelected(paymentId: number): boolean {
    return this.selectedPaymentIds.has(paymentId);
  }

  /**
   * Get allocated amount for a payment.
   */
  public getAllocatedAmount(paymentId: number): number {
    return this.allocatedAmounts.get(paymentId) || 0;
  }

  /**
   * Update allocated amount for a payment.
   */
  public onAllocatedAmountChange(lot: BankLetteringLot, event: Event): void {
    const target = event.target as HTMLInputElement;
    const val = parseFloat(target.value);
    const amount = isNaN(val) ? 0 : Math.max(0, val);

    if (amount === 0) {
      this.selectedPaymentIds.delete(lot.paymentId);
      this.allocatedAmounts.delete(lot.paymentId);
    } else {
      this.selectedPaymentIds.add(lot.paymentId);
      this.allocatedAmounts.set(lot.paymentId, Number(amount.toFixed(2)));
    }
    this.cdr.markForCheck();
  }

  /**
   * Select all currently filtered payments.
   */
  public selectAllPayments(): void {
    const list = this.filteredLots;
    let sum = 0;
    for (const lot of list) {
      this.selectedPaymentIds.add(lot.paymentId);
      const amt = Number(lot.amount) || 0;
      this.allocatedAmounts.set(lot.paymentId, amt);
      sum += amt;
    }

    if (!this.statement.totalAmount || this.statement.totalAmount === 0) {
      this.statement.totalAmount = Number(sum.toFixed(2));
    }
    this.cdr.markForCheck();
  }

  /**
   * Deselect all payments.
   */
  public deselectAllPayments(): void {
    this.selectedPaymentIds.clear();
    this.allocatedAmounts.clear();
    this.cdr.markForCheck();
  }

  /**
   * Get total allocated amount across all selected payments.
   */
  public getTotalAllocated(): number {
    let sum = 0;
    for (const id of Array.from(this.selectedPaymentIds)) {
      sum += this.allocatedAmounts.get(id) || 0;
    }
    return Number(sum.toFixed(2));
  }

  /**
   * Calculate balance difference: Statement Amount - Total Allocated.
   */
  public getBalanceDifference(): number {
    const statementAmt = Number(this.statement.totalAmount) || 0;
    const allocatedAmt = this.getTotalAllocated();
    return Number((statementAmt - allocatedAmt).toFixed(2));
  }

  /**
   * Check if bank statement is balanced with allocations.
   */
  public isBalanced(): boolean {
    const statementAmt = Number(this.statement.totalAmount) || 0;
    if (statementAmt <= 0) return false;
    if (this.selectedPaymentIds.size === 0) return false;
    return Math.abs(this.getBalanceDifference()) < 0.005;
  }

  /**
   * Reset filter.
   */
  public onResetFilters(): void {
    this.filter.search = '';
    this.filter.partFilter = 'ALL';
    this.cdr.markForCheck();
  }

  /**
   * Submit bank lettering and BOFIP fiscal seal.
   */
  public onSubmitLettering(): void {
    if (!this.isBalanced()) {
      this.alertService.error('Impossible de valider : l\'encaissement bancaire n\'est pas équilibré (écart !== 0,00 €).');
      return;
    }

    const user = this.loginService.loggeduser;
    const station = this.loginService.currentstation;

    if (!user || !user.id || !station || !station.id) {
      this.alertService.error('Erreur: Session utilisateur ou station de travail non identifiée.');
      return;
    }

    const count = this.selectedPaymentIds.size;
    const total = this.getTotalAllocated().toFixed(2);
    const dateStr = this.statement.dateReglement ? new DatePipe('fr-FR').transform(this.statement.dateReglement, 'dd/MM/yyyy') : '';

    const userId = Number(user.id);
    const stationId = Number(station.id);

    Swal.fire({
      title: 'Confirmer le rapprochement bancaire FSE ?',
      html: `
        <div style="text-align: left; font-size: 13.5px; line-height: 1.6;">
          <p><strong>Montant bancaire :</strong> ${total} €</p>
          <p><strong>Date de valeur :</strong> ${dateStr}</p>
          <p><strong>Nombre de virements tiers-payeurs :</strong> ${count}</p>
          <p><strong>Référence :</strong> ${this.statement.bankReference || 'Non renseignée'}</p>
          <div style="margin-top: 12px; padding: 10px; background-color: #f0fdf4; border-left: 3px solid #16a34a; border-radius: 4px; font-size: 12px; color: #166534;">
            🔒 <strong>Conformité fiscale BOFIP ISCA :</strong> Cette action va clôturer les créances AMO/AMC correspondantes et générer les empreintes de scellement cryptographique irréversibles.
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Valider et sceller',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#64748b'
    }).then((result) => {
      if (result.isConfirmed) {
        this.executeReconcileBatch(userId, stationId);
      }
    });
  }

  private executeReconcileBatch(userId: number, stationId: number): void {
    this.isSubmitting = true;
    this.spinner.show();
    this.cdr.markForCheck();

    const paymentIds = Array.from(this.selectedPaymentIds);
    const bankDateIso = this.statement.dateReglement
      ? new DatePipe('en-US').transform(this.statement.dateReglement, 'yyyy-MM-dd') || undefined
      : undefined;

    this.fseService.reconcileBankLetteringBatch$({
      paymentIds,
      userId,
      stationId,
      bankDate: bankDateIso,
      bankReference: this.statement.bankReference || undefined,
      note: this.statement.note || undefined
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting = false;
          this.spinner.hide();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.alertService.success('Rapprochement bancaire enregistré et scellé avec succès.');
          this.selectedPaymentIds.clear();
          this.allocatedAmounts.clear();
          this.statement.totalAmount = null;
          this.statement.bankReference = '';
          this.statement.note = '';
          this.loadRspTransfers();
          this.loadRspErrors();
        },
        error: (err) => {
          this.alertService.error('Erreur lors du rapprochement bancaire: ' + err.message);
        }
      });
  }
}
